import type { ReactNode } from "react";

import { cn } from "@sirdab/ui";

import { formatCount } from "../format/money";
import { useMessages } from "../i18n";

export interface StepHeaderProps {
  /** 1-based. */
  step: number;
  total: number;
  /** Already-localised short name for each step, in order, length `total`. */
  stepLabels: readonly string[];
  /** Already-localised title. */
  title: string;
  /** Already-localised supporting line, optional. */
  description?: string | undefined;
  children?: ReactNode;
}

/**
 * Title plus progress. docs/08 justifies a step tracker here because the content genuinely
 * is a sequence — each step gets its own named chip rather than a row of bare circles, so a
 * glance says both how many decisions are left and what they are. Labels hide below `sm`
 * (the 390px case the old bare-dot version was built for): six chips with Arabic labels
 * don't fit a small phone, so the dot alone carries the state there and the current step's
 * name still reads from the heading just below.
 */
export function StepHeader({
  step,
  total,
  stepLabels,
  title,
  description,
  children,
}: StepHeaderProps): React.JSX.Element {
  const { locale, t } = useMessages();

  return (
    <header className="flex flex-col gap-3">
      {/* Chips rather than one filled bar: at a glance it says how many named decisions
          are left, not just how far along she is. Logical order, so it flips with `dir`. */}
      <div className="flex items-center gap-3">
        <div
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={total}
          className="flex flex-1 items-center gap-1"
        >
          {stepLabels.map((label, i) => {
            const index = i + 1;
            const isDone = index < step;
            const isActive = index === step;
            return (
              <div
                key={label}
                className={cn(
                  "flex flex-1 flex-col items-center gap-1 rounded-full py-1.5 transition-colors duration-300",
                  isActive ? "bg-primary/10" : "bg-transparent",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full transition-colors duration-300",
                    isDone || isActive ? "bg-primary" : "bg-muted",
                  )}
                />
                <span
                  className={cn(
                    "text-caption hidden truncate font-medium sm:block",
                    isActive ? "text-primary" : "text-subtle",
                  )}
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-caption tabular text-subtle shrink-0">
          <span className="me-1">{t("ui.step_of")}</span>
          <span dir="ltr">
            {formatCount(step, locale)}/{formatCount(total, locale)}
          </span>
        </p>
      </div>
      <h1 className="text-heading-sm text-foreground">{title}</h1>
      {description !== undefined ? (
        <p className="text-body text-muted-foreground">{description}</p>
      ) : null}
      {children}
    </header>
  );
}
